import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import LabeledControl from "@editor/components/common/designSystem/LabeledControl";

import { useProductFormEdit } from "@/features/products/hooks/useProductFormEdit";
import IconButton from "@replo/design-system/components/button/IconButton";
import Separator from "@replo/design-system/components/Separator";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import twMerge from "@replo/design-system/utils/twMerge";
import { CircleMinus, Plus } from "lucide-react";
import { animated, config, useSpring } from "react-spring";

import ProductFilesDropZone from "./ProductFilesDropZone";

export function ProductDeliveryEmail() {
  const { watch, dispatchProductEdit, errors, isSubmitted, hasDataChanged } =
    useProductFormEdit();

  const productEmail = watch("productEmail");
  const hasFulfillmentEmail = watch("hasFulfillmentEmail");

  const contentAnimation = useSpring({
    config: config.stiff,
    opacity: hasFulfillmentEmail ? 1 : 0,
    transform: hasFulfillmentEmail ? "scaleY(1)" : "scaleY(0.95)",
  });

  const handleToggle = () => {
    dispatchProductEdit({
      type: "changeProperty",
      payload: { key: "hasFulfillmentEmail", value: !hasFulfillmentEmail },
    });
  };

  const updateProductEmail = (updates: {
    header?: string;
    message?: string;
    buttonText?: string;
    buttonLink?: string;
  }) => {
    dispatchProductEdit({
      type: "updateProductEmail",
      payload: {
        productEmail: {
          header: productEmail?.header ?? "",
          message: productEmail?.message,
          buttonText: productEmail?.buttonText,
          buttonLink: productEmail?.buttonLink,
          ...updates,
        },
        shouldDirty: true,
      },
    });
  };

  return (
    <div
      className={twMerge(
        "border-0.5 border-border rounded-[10px] bg-white flex flex-col items-center p-5",
        hasFulfillmentEmail && "gap-4",
      )}
    >
      <div className="flex flex-row justify-between items-center p-0 gap-1 w-full">
        <div className="flex flex-row items-center gap-4">
          <div className="flex items-center flex-shrink-0">
            <img
              src="/images/products/checkout-delivery.svg"
              alt="Product delivery email"
              aria-hidden
              role="presentation"
              className="w-10 h-10 flex-shrink-0"
            />
          </div>
          <div className="flex flex-col items-start p-0 gap-1">
            <h4 className="header-h4 text-default">Product Delivery Email</h4>
            <p className="text-base text-muted">
              Automatically send customers files or links after a purchase is
              made.
            </p>
          </div>
        </div>
        <IconButton
          variant="tertiary"
          icon={
            hasFulfillmentEmail ? <CircleMinus size={14} /> : <Plus size={14} />
          }
          size="sm"
          aria-label={
            hasFulfillmentEmail
              ? "Remove product delivery email"
              : "Add product delivery email"
          }
          onClick={handleToggle}
        />
      </div>

      <animated.div
        style={{
          ...contentAnimation,
          overflow: "hidden",
        }}
        className="w-full"
      >
        {hasFulfillmentEmail && (
          <div className="flex flex-col gap-4 w-full">
            <Separator />
            <LabeledControl
              label="Header"
              size="base"
              containerClassName="w-full"
              error={
                (isSubmitted || hasDataChanged) && errors.productEmail?.header
                  ? errors.productEmail.header.message
                  : undefined
              }
              required
            >
              <Input
                value={productEmail?.header ?? ""}
                onChange={(e) => updateProductEmail({ header: e.target.value })}
                placeholder="Your file is ready!"
                size="base"
                layoutClassName="w-full"
                validityState={
                  (isSubmitted || hasDataChanged) && errors.productEmail?.header
                    ? "invalid"
                    : "valid"
                }
              />
            </LabeledControl>
            <LabeledControl
              label="Message"
              size="base"
              containerClassName="w-full"
            >
              <Textarea
                value={productEmail?.message ?? ""}
                onChange={(value) => updateProductEmail({ message: value })}
                placeholder="Your templates are ready for download!"
                size="base"
                textLength="long"
                layoutClassName="w-full"
              />
            </LabeledControl>

            <div className="flex gap-6 w-full">
              <LabeledControl
                label="Button text"
                size="base"
                containerClassName="flex-1 min-w-0"
              >
                <Input
                  value={productEmail?.buttonText ?? ""}
                  onChange={(e) =>
                    updateProductEmail({ buttonText: e.target.value })
                  }
                  placeholder="Access product..."
                  size="base"
                  layoutClassName="w-full"
                />
              </LabeledControl>
              <LabeledControl
                label="Button link"
                size="base"
                containerClassName="flex-1 min-w-0"
              >
                <Input
                  value={productEmail?.buttonLink ?? ""}
                  onChange={(e) =>
                    updateProductEmail({ buttonLink: e.target.value })
                  }
                  placeholder="https://private-link"
                  size="base"
                  layoutClassName="w-full"
                />
              </LabeledControl>
            </div>

            <ProductFilesDropZone />
          </div>
        )}
      </animated.div>
    </div>
  );
}
